/**
 * Whether the pointer, moving over the navigation rail, is heading for the menu
 * open beside it (the "menu aim" Amazon's mega-menu made standard). Heading
 * there, it may cross other items on the way, and the menu stays; anywhere
 * else (straight down the rail, back left) it is scanning, and the item under
 * it takes over at once.
 */

export interface PointerSample {
  x: number
  y: number
  /** The event's timeStamp. */
  t: number
}

/** The open menu's near edge, in viewport pixels. */
export interface MenuEdge {
  left: number
  top: number
  bottom: number
}

/** How far back the direction is read from, so one jittery event doesn't decide it. */
export const AIM_SAMPLE_MS = 50
/** Samples older than this say nothing about where the pointer is heading now. */
const TRAIL_MAX_AGE_MS = 300
/** Less rightward travel than this is a hand drifting while it scans down. */
export const AIM_MIN_TRAVEL_PX = 3
/**
 * Rightward travel per pixel of vertical travel, at least. From Users at the
 * bottom of an icons-only rail, aiming at its menu's rows runs from about 1
 * across for 4.5 up (the first row) to 1 for 3; a hand scanning down drifts
 * under 1 for 10.
 */
export const AIM_MIN_RIGHTWARD_RATIO = 0.15
/** The menu's corners count this much further out, for a path that's almost there. */
export const AIM_CORNER_SLACK_PX = 24

/** The trail with this sample added and the stale ones gone. */
export function addPointerSample(trail: readonly PointerSample[], sample: PointerSample) {
  return [...trail.filter((point) => sample.t - point.t <= TRAIL_MAX_AGE_MS), sample].slice(-12)
}

/**
 * Where the pointer was a moment before `now`: the newest sample at least
 * AIM_SAMPLE_MS old, else the oldest earlier one; null with nothing earlier.
 */
export function pointerSampleBefore(trail: readonly PointerSample[], now: number) {
  const earlier = trail.filter((point) => point.t < now)
  for (let index = earlier.length - 1; index >= 0; index -= 1) {
    if (now - earlier[index].t >= AIM_SAMPLE_MS) return earlier[index]
  }
  return earlier[0] ?? null
}

const cross = (a: { x: number; y: number }, b: { x: number; y: number }) => a.x * b.y - a.y * b.x

/**
 * Moving from `from` to `to`, is the pointer heading into the triangle between
 * `from` and the menu's near corners?
 */
export function isAimingAtMenu(
  from: { x: number; y: number },
  to: { x: number; y: number },
  menu: MenuEdge
) {
  const move = { x: to.x - from.x, y: to.y - from.y }
  if (move.x < AIM_MIN_TRAVEL_PX) return false
  if (move.x < AIM_MIN_RIGHTWARD_RATIO * Math.abs(move.y)) return false
  if (from.x >= menu.left) return false

  // Screen y grows downward, so the move lies between the two corner rays
  // when it turns clockwise from the top one and counterclockwise from the
  // bottom one.
  const toTop = { x: menu.left - from.x, y: menu.top - AIM_CORNER_SLACK_PX - from.y }
  const toBottom = { x: menu.left - from.x, y: menu.bottom + AIM_CORNER_SLACK_PX - from.y }
  return cross(toTop, move) >= 0 && cross(move, toBottom) >= 0
}
